import Box from "@mui/material/Box"
import {
    EEmailLogType,
    EEmailProviders,
    EmailAPIFactory,
    EmptyContent,
    formatNumber,
    FullBar,
    GenericWrapperContext,
    getDate,
    getStartOfDate,
    Header,
    LoadingContainer,
    TEmailReport,
    Typography
} from "@cakemail-org/ui-components-v2"
import { ChartData } from "chart.js"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Charts } from "../../charts"
import "./styles.scss"
import "../styles.scss"
import { TEmailSendVolume, TEmailSendVolumenActiveMetric } from "./types"
import { ColorIcon } from "../../ColorIcon"
import { GraphContainer } from "../../graphContainer"
import { Stack, useTheme } from "@mui/material"
import { EChartExtraColor } from "../../charts/types"
import { abbreviateNumber, getProvidersToFetch, returnEmailTagsQuery, returnProviderQuery } from "../../../utils/data"
import { TEmailAPIDeliverabilityProviderReport } from "../../../container/emailAPI/containers/types"
import { emptyReportLog } from "../../../data/emailAPI"
import { getAPIDateRange } from "../../../utils/date"
import { LastXDays } from "../../lastXDays"

export function EmailSendVolume({ timeRange, tagsFilter, providerFilter = [], controlled, controlledProps, filterOperator, refreshKey }: TEmailSendVolume) {
    const genericWrapper = useContext(GenericWrapperContext)
    const theme = useTheme()
    const { t, i18n } = useTranslation()

    const [activeMetrics, setActiveMetrics] = useState<TEmailSendVolumenActiveMetric>({
        delivered: true,
        bounce: true
    })

    const [stateMetrics, setStateMetrics] = useState<TEmailAPIDeliverabilityProviderReport>()
    const [stateFullMetrics, setStateFullMetrics] = useState<TEmailReport>()
    const [stateLoading, setStateLoading] = useState<boolean>(true)
    const [stateError, setStateError] = useState<boolean>(false)

    let loading: boolean = false
    let error: boolean = false
    let metrics: TEmailAPIDeliverabilityProviderReport | undefined = undefined
    let fullMetrics: TEmailReport | undefined = undefined

    // To aliviate multiple same component on a page, will minimize the ammount of api call. 
    // You cannot change the controlled property afterwards
    if (controlled) {
        metrics = controlledProps?.metrics
        fullMetrics = controlledProps?.fullMetrics
        loading = controlledProps?.loading || false
        error = controlledProps?.error || false
    } else {
        useEffect(fetchReports, [timeRange, tagsFilter, providerFilter, refreshKey])
        useEffect(fetchFullReport, [timeRange, tagsFilter, providerFilter, refreshKey])
        metrics = stateMetrics
        fullMetrics = stateFullMetrics
        loading = stateLoading
        error = stateError
    }

    function handleActiveMetric(metric: EEmailLogType) {
        setActiveMetrics({
            ...activeMetrics,
            [metric]: !activeMetrics[metric]
        })
    }

    function getDefaultFetchParams() {
        const apiTimeRange = getAPIDateRange({ range: timeRange })
        return {
            tags: returnEmailTagsQuery(tagsFilter, { operator: filterOperator }),
            startTime: apiTimeRange.start,
            endTime: apiTimeRange.end
        }
    }

    function fetchReports() {
        setStateLoading(true)
        const promisses: any[] = []
        const defaultParams = getDefaultFetchParams()
        const providersToFetch = getProvidersToFetch(providerFilter)

        providersToFetch.forEach((p: EEmailProviders) => {
            promisses.push(EmailAPIFactory.getReport({
                providers: returnProviderQuery({ providers: p, operator: filterOperator }),
                ...defaultParams
            }).then((data) => {
                return {
                    provider: p,
                    data
                }
            }))
        })
        Promise.allSettled(promisses).then((results) => {
            const newMetrics: TEmailAPIDeliverabilityProviderReport = {}
            let hasOnlyErrors = true
            try {
                results.forEach((res) => {
                    if (res.status === "fulfilled") {
                        hasOnlyErrors = false
                        const entry: { provider: EEmailProviders, data: TEmailReport[] } = res.value
                        newMetrics[entry.provider] = entry.data[0] || emptyReportLog
                    }
                })
            } catch (e) {
                hasOnlyErrors = true
            }

            if (hasOnlyErrors) {
                setStateError(true)
            } else {
                setStateMetrics(newMetrics)
            }

            setStateLoading(false)
        })

    }

    function fetchFullReport() {
        EmailAPIFactory.getReport({
            ...getDefaultFetchParams(),
            providers: returnProviderQuery({ providers: getProvidersToFetch(providerFilter), operator: filterOperator }),
        }).then((data) => {
            setStateFullMetrics(data[0])
        })
    }

    function sortMetrics(obj: TEmailAPIDeliverabilityProviderReport): TEmailAPIDeliverabilityProviderReport {
        const entries = Object.entries(obj)
        const sortedEntries = entries.sort((a, b) => b[1]?.delivered - a[1]?.delivered)
        return Object.fromEntries(sortedEntries)
    }

    const bounceSet: number[] = []
    const deliveredSet = Object.entries(sortMetrics(metrics || {}))?.map(([p, r]) => {
        bounceSet.push(r?.bounce)
        return r?.delivered
    }) || []

    const labelsTranslated = Object.keys(sortMetrics(metrics || {}))
    const index = labelsTranslated.indexOf(EEmailProviders.other)

    if (index !== -1) {
        labelsTranslated[index] = t("email-log-type-other")
    }
    const chartData: ChartData = {
        labels: labelsTranslated,
        datasets: []
    }

    if (activeMetrics.delivered) {
        chartData.datasets.push({
            data: deliveredSet,
            backgroundColor: EChartExtraColor.deepBlue,
        })
    }
    if (activeMetrics.bounce) {
        chartData.datasets.push({
            data: bounceSet,
            backgroundColor: theme.palette.wildSand.main,
        })
    }

    //Override default options
    const options = {
        indexAxis: 'y',
        plugins: {
            tooltip: {
                mode: "y",
                callbacks: {
                    label: (context: any) => {
                        return `${context.dataset?.metadata?.provider || ""} : ${context.formattedValue}`
                    },
                }
            }
        },
        scales: {
            x: {
                stacked: true,
                grid: {
                    display: true
                },
                ticks: {
                    callback: function (tickValue: number) {
                        return abbreviateNumber(tickValue)
                    }
                }
            },
            y: {
                stacked: true,
                grid: {
                    display: false
                }
            }
        }
    }

    const apiTimeRange = getAPIDateRange({ range: timeRange })

    return (
        <Stack className="analyticBlock emailSendVolume">
            <Header
                heading={t("email-send-volume-heading")}
                postHeading={<LastXDays apiTimeRange={apiTimeRange} />}
                headingTooltip={t("email-send-volume-heading-tooltip")}
            />

            <LoadingContainer loading={!metrics && !error} variant="linear">
                {metrics && <GraphContainer loading={loading}>
                    <FullBar
                        left={<>
                            <Typography variant="titleS"> {fullMetrics ? formatNumber(fullMetrics.submitted, genericWrapper.user?.language) : "-"} {t("general-emails-sent-txt")}</Typography>
                            <Typography variant="titleS"> {fullMetrics ? formatNumber(fullMetrics.delivered, genericWrapper.user?.language) : "-"} {t("general-delivered-txt")}</Typography>
                            <Typography variant="titleS"> {fullMetrics ? formatNumber(fullMetrics.bounce, genericWrapper.user?.language) : "-"} {t("general-bounced-txt")}</Typography>
                        </>}
                        right={<>
                            <Stack onClick={() => { handleActiveMetric(EEmailLogType.delivered) }} className={`pointer ${activeMetrics.delivered ? "active" : "inactive"}`} direction="row" alignItems="center">
                                <ColorIcon color={EChartExtraColor.deepBlue} /><Typography variant="body1S" className="semiBold"> {t("general-delivered")}</Typography>
                            </Stack>
                            <Stack onClick={() => { handleActiveMetric(EEmailLogType.bounce) }} className={`pointer ${activeMetrics.bounce ? "active" : "inactive"}`} direction="row" alignItems="center">
                                <ColorIcon color="wildSand" /><Typography variant="body1S" className="semiBold"> {t("general-bounced")}</Typography>
                            </Stack>
                        </>}
                    />
                    {/*@ts-ignore*/}
                    <Charts type="bar" data={chartData} height={"18rem"} options={options} />
                </GraphContainer>}
                {error && <EmptyContent
                    title={t("analytic-block-loading-error-title")}
                />}
            </LoadingContainer>
        </Stack>
    )
}